/**
 * The typed client factory.
 *
 * One `createClient` call for the website, the admin panel, the mobile app and
 * any Node script, so that the three surfaces cannot drift on session handling —
 * which is the thing that silently breaks in exactly one of them and is found by
 * a user rather than by CI.
 *
 * KEYS ARE NEVER EMBEDDED HERE. They are arguments. `packages/api` is imported
 * by three bundlers with three different environment-variable conventions
 * (`VITE_*`, `EXPO_PUBLIC_*`, plain `process.env`), and a package that reads any
 * one of them either breaks in the other two or, worse, gets a value inlined at
 * build time that was never meant to ship. CLAUDE.md hard rule 8.
 */

import {
  createClient,
  type SupabaseClient,
  type SupabaseClientOptions,
} from '@supabase/supabase-js'
import type { Database } from '@avexa/types'
import { ERROR_CODES } from '@avexa/types'
import { avexaError } from './errors'
import type { AvexaClient } from './internal'

export type { AvexaClient }

type PublicClientOptions = SupabaseClientOptions<'public'>

/**
 * The session store. Web supplies nothing (supabase-js finds `localStorage`);
 * React Native supplies `AsyncStorage` or `expo-secure-store`; Node supplies
 * nothing and does not persist.
 */
export type AvexaSessionStorage = NonNullable<PublicClientOptions['auth']>['storage']

/** `typeof fetch`, taken from supabase-js so this file needs no DOM lib. */
export type AvexaFetch = NonNullable<PublicClientOptions['global']>['fetch']

export interface AvexaClientOptions {
  /**
   * Where the session is kept. Required on React Native — without it the user
   * is signed out every time the app is killed, which is the single most common
   * Expo + Supabase mistake.
   */
  storage?: AvexaSessionStorage
  /** Namespaces the stored session. Set it when two builds share an origin. */
  storageKey?: string
  /** Default: true everywhere except when `persistSession` is false. */
  autoRefreshToken?: boolean
  /** Default: true in a browser or when `storage` is supplied; false in Node. */
  persistSession?: boolean
  /**
   * OAuth callback parsing. Default: true in a browser, false everywhere else.
   * On React Native there is no URL fragment to read and the default `true`
   * makes auth-js reach for `window.location`.
   */
  detectSessionInUrl?: boolean
  /** Merged into every PostgREST, Storage and Functions request. */
  headers?: Record<string, string>
  /** Inject a fetch implementation (a test double, or an instrumented one). */
  fetch?: AvexaFetch
  /**
   * Realtime message ceiling. The default of 10/s is right for a chat thread;
   * raise it only with a reason.
   */
  realtimeEventsPerSecond?: number
}

// ─── Runtime detection ──────────────────────────────────────────────────────

/**
 * A browser is a global with BOTH a `document` and a `location`. React Native
 * has a `location`-shaped global in some Expo versions but never a `document`,
 * which is why both are checked rather than either.
 */
function isBrowser(): boolean {
  const global = globalThis as unknown as Record<string, unknown>
  return typeof global['document'] === 'object' && typeof global['location'] === 'object'
}

// ─── Key safety ─────────────────────────────────────────────────────────────

const BASE64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'

/**
 * Minimal base64url → UTF-8-ish decoder.
 *
 * Hand-written because `atob` is absent from Hermes and `Buffer` is absent from
 * a browser, and this package must behave identically in both. Only ever fed a
 * JWT payload, which is ASCII JSON, so no multi-byte handling is needed — and a
 * failure returns null rather than throwing, because a malformed key must
 * produce the "this is not a JWT" path and not a crash inside a constructor.
 */
function decodeBase64Url(input: string): string | null {
  let bits = 0
  let value = 0
  let out = ''
  for (const character of input) {
    const index = BASE64URL_ALPHABET.indexOf(character)
    if (index === -1) return null
    value = (value << 6) | index
    bits += 6
    if (bits >= 8) {
      bits -= 8
      out += String.fromCharCode((value >> bits) & 0xff)
    }
  }
  return out
}

/**
 * True when the string is a privileged Supabase key.
 *
 * Two formats exist: the legacy JWT, whose payload carries `"role":"service_role"`,
 * and the newer `sb_secret_…` publishable-key replacement. Both are checked.
 *
 * WHY THIS RUNS AT ALL. `.env` is committed in this repository (CLAUDE.md § 7),
 * so the service-role key and the anon key sit four lines apart in the same
 * file, and a copy-paste that swaps them produces a client that WORKS — it just
 * bypasses every RLS policy in the schema and ships in the bundle. Nothing else
 * in the stack catches that. It costs one string comparison at startup.
 */
export function isPrivilegedKey(key: string): boolean {
  if (key.startsWith('sb_secret_')) return true

  const segments = key.split('.')
  const claims = segments.length === 3 ? segments[1] : undefined
  if (claims === undefined) return false

  const json = decodeBase64Url(claims)
  if (json === null) return false

  try {
    const parsed: unknown = JSON.parse(json)
    if (typeof parsed !== 'object' || parsed === null) return false
    const role = (parsed as Record<string, unknown>)['role']
    return role === 'service_role'
  } catch {
    return false
  }
}

// ─── Factory ────────────────────────────────────────────────────────────────

/**
 * Builds the one client the rest of this package takes as its first argument.
 *
 * @param url      Supabase project URL, e.g. `https://<ref>.supabase.co`
 * @param anonKey  The ANON / publishable key. Never the service-role key.
 */
export function createAvexaClient(
  url: string,
  anonKey: string,
  options: AvexaClientOptions = {},
): AvexaClient {
  if (typeof url !== 'string' || url.length === 0) {
    throw avexaError(ERROR_CODES.INTERNAL_ERROR, 'Supabase URL is missing')
  }
  if (typeof anonKey !== 'string' || anonKey.length === 0) {
    throw avexaError(ERROR_CODES.INTERNAL_ERROR, 'Supabase anon key is missing')
  }
  if (isPrivilegedKey(anonKey)) {
    throw avexaError(
      ERROR_CODES.INTERNAL_ERROR,
      'Refusing to build a client with a service-role key. That key bypasses every ' +
        'RLS policy and must never leave an Edge Function (CLAUDE.md hard rule 8).',
    )
  }

  const browser = isBrowser()
  // Persist by default wherever there is somewhere to persist TO. A Node script
  // that persisted would leave a session file behind on a build agent.
  const persistSession = options.persistSession ?? (browser || options.storage !== undefined)

  const clientOptions: PublicClientOptions = {
    auth: {
      persistSession,
      // Refreshing without persisting keeps a timer alive around a session that
      // is thrown away, which in Node keeps the process from exiting.
      autoRefreshToken: options.autoRefreshToken ?? persistSession,
      detectSessionInUrl: options.detectSessionInUrl ?? browser,
      ...(options.storage !== undefined ? { storage: options.storage } : {}),
      ...(options.storageKey !== undefined ? { storageKey: options.storageKey } : {}),
    },
    global: {
      headers: {
        // Named so a slow query can be traced back to this layer in the
        // Supabase logs rather than to "some client".
        'x-avexa-client': 'packages/api',
        ...options.headers,
      },
      ...(options.fetch !== undefined ? { fetch: options.fetch } : {}),
    },
    realtime: {
      params: { eventsPerSecond: options.realtimeEventsPerSecond ?? 10 },
    },
  }

  return createClient<Database>(url, anonKey, clientOptions)
}

/**
 * The signed-in user's id, or null.
 *
 * Reads the locally cached session rather than calling `getUser()`, which is a
 * network round trip to the auth server. Every "my own rows" function in this
 * package takes the id as an argument instead of calling this, so that a screen
 * rendering six queries does not resolve the same session six times — but the
 * helper is here for the one place that needs it: the app's session bootstrap.
 */
export async function getCurrentUserId(db: SupabaseClient<Database>): Promise<string | null> {
  const { data, error } = await db.auth.getSession()
  if (error) return null
  return data.session?.user.id ?? null
}
